// P1-7 (Agent D): Employee workflows — full list surface with
//   - Add employee modal (idempotency-key per intent)
//   - Bulk import (preview + import, idempotency on import)
//   - Row link to /employees/:id (EmployeeDetailPage)
//   - Row actions: Edit + Suspend / Reactivate (idempotency)
// Sync status + last_sync_error are surfaced honestly per row.

import { SearchInput } from '../components/SearchInput.js';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { getAllEmployees } from '../lib/employees.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';
import {
  credentialTypeShortLabel,
  employeeRoleLabel,
  statusLabel,
} from '../lib/humanLabels.js';
import { CredentialPills } from '../components/CredentialPills.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { useAuth } from '../auth/AuthContext.js';
import { Field, ModalShell } from '../components/Modal.js';
import { EnrollFobModal } from '../components/EnrollFobModal.js';


type EmployeeStatus = 'invited' | 'active' | 'suspended' | 'offboarded';
type SyncStatus = 'synced' | 'pending' | 'failed' | 'drifted' | 'in_flight';
type EmployeeRole = 'employee' | 'manager';

interface AccessActivitySummary {
  event_id: string;
  type: string;
  severity: string;
  occurred_at: string;
  door_name: string | null;
  unifi_device_id: string | null;
}

interface EmployeeRow {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  role: EmployeeRole;
  status: EmployeeStatus;
  unifi_user_id: string | null;
  sync_status: SyncStatus;
  last_sync_error: string | null;
  schedule_id: string | null;
  credential_types: string[];
  last_access: AccessActivitySummary | null;
}

interface ScheduleSummary {
  id: string;
  name: string;
}

interface AccessZoneSummary {
  id: string;
  name: string;
  schedule_id: string | null;
  sync_status: SyncStatus;
  controller_enforced: boolean;
  assigned: boolean;
  source_access_zone_id: string | null;
}

const STATUS_TONE: Record<EmployeeStatus, string> = {
  invited: 'bg-tertiary-fixed text-on-tertiary-fixed',
  active: 'bg-secondary-container text-on-secondary-container',
  suspended: 'bg-error-container text-on-error-container',
  offboarded: 'bg-surface-container-high text-on-surface-variant',
};

const ROLE_PILL: Record<string, string> = {
  manager:        'bg-secondary-container text-on-secondary-container',
  tenant_admin:   'bg-primary-container text-on-primary-container',
  tenant_manager: 'bg-secondary-container text-on-secondary-container',
  tenant_viewer:  'bg-surface-container-high text-on-surface-variant',
  employee:       'bg-surface-container text-on-surface-variant',
  resident:       'bg-surface-container text-on-surface-variant',
};

function RolePill({ role }: { role: string }) {
  // eslint-disable-next-line security/detect-object-injection
  const tone = ROLE_PILL[role] ?? 'bg-surface-container text-on-surface-variant';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${tone}`}>
      {employeeRoleLabel(role)}
    </span>
  );
}


async function loadEmployees(scopeOpts?: ApiRequestInit): Promise<EmployeeRow[]> {
  const res = await getAllEmployees<EmployeeRow>(scopeOpts);
  return res.data;
}

async function loadSchedules(scopeOpts?: ApiRequestInit): Promise<ScheduleSummary[]> {
  try {
    const res = await apiClient.get<{ data: ScheduleSummary[] }>('/api/v1/schedules', scopeOpts);
    return res.data;
  } catch {
    return [];
  }
}

async function loadAccessZones(scopeOpts?: ApiRequestInit): Promise<AccessZoneSummary[]> {
  try {
    const res = await apiClient.get<{
      data: { assigned: AccessZoneSummary[]; available: AccessZoneSummary[] };
    }>('/api/v1/employees/access-zones', scopeOpts);
    return [...res.data.assigned, ...res.data.available];
  } catch {
    return [];
  }
}

type RowActionKind = 'suspend' | 'reactivate' | 'offboard';

function activityTime(value: string): string {
  return new Date(value).toLocaleString();
}

function ActivitySummary({ activity }: { activity: AccessActivitySummary | null }) {
  if (!activity) {
    return <span className="font-body-md text-body-md text-on-surface-variant">Never Accessed</span>;
  }
  return (
    <span className="font-body-md text-body-md text-on-surface">
      {activityTime(activity.occurred_at)}
      <span className="text-on-surface-variant"> · {activity.door_name ?? 'Unknown door'}</span>
    </span>
  );
}

export function EmployeesPage() {
  const { state } = useAuth();
  const {
    tenantName,
    scopeOpts,
    effectiveTenantId,
    canShowTenantActions,
    canExecuteTenantActions,
    canLoadData,
    isMasterWithoutContext,
    scopedDetailHref,
  } = useTenantScopedPage();
  const [rows, setRows] = useState<EmployeeRow[] | null>(null);
  const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
  const [accessZones, setAccessZones] = useState<AccessZoneSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [rowAction, setRowAction] = useState<{
    kind: RowActionKind;
    employee: EmployeeRow;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (state.status !== 'authed') return;
    let cancelled = false;
    if (!canLoadData || !effectiveTenantId) {
      setRows([]);
      setSchedules([]);
      setAccessZones([]);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    setRows(null);
    setError(null);
    Promise.all([loadEmployees(scopeOpts), loadSchedules(scopeOpts), loadAccessZones(scopeOpts)])
      .then(([emps, scheds, zones]) => {
        if (cancelled) return;
        setRows(emps);
        setSchedules(scheds);
        setAccessZones(zones);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load employees');
      });
    return () => {
      cancelled = true;
    };
  }, [canLoadData, effectiveTenantId, reloadTick, scopeOpts, state.status]);

  const [search, setSearch] = useState('');
  const filteredRows = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    if (q.length === 0) return rows;
    return rows.filter((e) => {
      const lower = q;
      const nameOrEmailMatch =
        e.name.toLowerCase().includes(lower) ||
        e.email.toLowerCase().includes(lower);
      const credMatch =
        lower === 'no credentials'
          ? (e.credential_types ?? []).length === 0
          : (e.credential_types ?? []).some((t) =>
              credentialTypeShortLabel(t).toLowerCase().includes(lower),
            );
      return nameOrEmailMatch || credMatch;
    });
  }, [rows, search]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="badge" className="text-2xl" />
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">Employees</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {tenantName
                ? `People with portal accounts or credentials at ${tenantName}.`
                : 'People with portal accounts or credentials at this tenant.'}
            </p>
          </div>
        </div>
        {canShowTenantActions ? (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              disabled={!canExecuteTenantActions}
              className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 bg-surface-container text-on-surface font-label-md text-label-md rounded-lg hover:bg-surface-container-high transition-all disabled:opacity-50 disabled:pointer-events-none"
              data-testid="employees-bulk-import-btn"
            >
              <Icon name="upload_file" />
              <span>Bulk import</span>
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              disabled={!canExecuteTenantActions}
              className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:pointer-events-none"
              data-testid="employees-add-btn"
            >
              <Icon name="person_add" />
              <span>Add employee</span>
            </button>
          </div>
        ) : null}
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="underline font-medium"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {isMasterWithoutContext ? (
        <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-6">
          <h2 className="font-title-md text-title-md text-on-surface">Choose a tenant</h2>
          <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
            Open a tenant from the Tenants page to manage its employees and credentials.
          </p>
          <Link
            to="/tenants"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-label-md text-label-md text-on-primary"
          >
            <Icon name="apartment" />
            Tenants
          </Link>
        </section>
      ) : null}

      {!isMasterWithoutContext && rows !== null && (rows.length > 0 || search.length > 0) ? (
        <div className="flex items-center gap-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search employees by name or email"
            id="employees-search"
          />
          {search.length > 0 && filteredRows ? (
            <span className="font-body-md text-body-md text-on-surface-variant">
              {filteredRows.length} of {rows.length}
            </span>
          ) : null}
        </div>
      ) : null}

      {!isMasterWithoutContext ? (
      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div className="p-6 text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : rows == null ? (
          <div className="p-12 flex justify-center" role="status" aria-live="polite">
            <Spinner />
            <span className="sr-only">Loading employees…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            No employees yet. Click <strong>Add employee</strong> to create one.
          </div>
        ) : filteredRows && filteredRows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            No employees match "{search}". Try a shorter or different search.
          </div>
        ) : (
          <>
            {/* Mobile card stack */}
            <div className="md:hidden divide-y divide-outline-variant">
              {(filteredRows ?? []).map((e) => (
                <div key={e.id} className="px-4 py-3 space-y-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-on-surface">{e.name}</p>
                      <p className="text-xs text-on-surface-variant">{e.email}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {scopedDetailHref('/employees', e.id, undefined, e.tenant_id) ? (
                        <Link
                          to={scopedDetailHref('/employees', e.id, undefined, e.tenant_id)!}
                          className="text-sm text-primary font-medium"
                        >
                          Edit
                        </Link>
                      ) : null}
                      {e.status === 'suspended' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setRowAction({ kind: 'reactivate', employee: e })}
                            disabled={!canExecuteTenantActions}
                            className="text-sm text-primary font-medium disabled:opacity-50"
                          >
                            Reactivate
                          </button>
                          <button
                            type="button"
                            onClick={() => setRowAction({ kind: 'offboard', employee: e })}
                            disabled={!canExecuteTenantActions}
                            className="text-sm text-error font-medium disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </>
                      ) : e.status !== 'offboarded' ? (
                        <button
                          type="button"
                          onClick={() => setRowAction({ kind: 'suspend', employee: e })}
                          disabled={!canExecuteTenantActions}
                          className="text-sm text-error font-medium disabled:opacity-50"
                        >
                          Suspend
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <RolePill role={e.role} />
                    <span className="text-xs text-on-surface-variant">·</span>
                    <span className="text-xs text-on-surface-variant">{statusLabel(e.status)}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-on-surface-variant">Last activity: </span>
                    <ActivitySummary activity={e.last_access} />
                  </div>
                  <CredentialPills types={e.credential_types ?? []} />
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-surface-container">
                  <tr>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[22%]">
                      Name
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3">
                      Email
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[10%]">
                      Role
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[12%]">
                      Status
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[18%]">
                      Credentials
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[20%]">
                      Last Activity
                    </th>
                    <th className="text-right font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[12%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {(filteredRows ?? []).map((e) => (
                    <tr
                      key={e.id}
                      data-testid={`employee-row-${e.id}`}
                      className="hover:bg-surface-container-low transition-colors"
                    >
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        {scopedDetailHref('/employees', e.id, undefined, e.tenant_id) ? (
                          <Link
                            to={scopedDetailHref('/employees', e.id, undefined, e.tenant_id)!}
                            className="text-primary hover:underline"
                          >
                            {e.name}
                          </Link>
                        ) : (
                          <span>{e.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        {e.email}
                      </td>
                      <td className="px-4 py-3">
                        <RolePill role={e.role} />
                      </td>
                      <td className="px-4 py-3">
                        {/* items-start: a flex column stretches its children by
                            default, so the status pill filled the whole column
                            and read as a progress bar rather than a badge. */}
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${STATUS_TONE[e.status]}`}
                          >
                            {statusLabel(e.status)}
                          </span>
                          {e.last_sync_error ? (
                            <span
                              className="font-label-sm text-label-sm text-error"
                              title={e.last_sync_error}
                              data-testid={`employee-row-error-${e.id}`}
                            >
                              {e.last_sync_error.length > 40
                                ? `${e.last_sync_error.slice(0, 40)}…`
                                : e.last_sync_error}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <CredentialPills types={e.credential_types ?? []} />
                      </td>
                      <td className="px-4 py-3">
                        <ActivitySummary activity={e.last_access} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {scopedDetailHref('/employees', e.id, undefined, e.tenant_id) ? (
                            <Link
                              to={scopedDetailHref('/employees', e.id, undefined, e.tenant_id)!}
                              className="text-sm text-primary hover:underline font-medium"
                            >
                              Edit
                            </Link>
                          ) : null}
                          {e.status === 'suspended' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setRowAction({ kind: 'reactivate', employee: e })}
                                disabled={!canExecuteTenantActions}
                                className="text-sm text-primary hover:underline font-medium disabled:opacity-50"
                              >
                                Reactivate
                              </button>
                              <button
                                type="button"
                                onClick={() => setRowAction({ kind: 'offboard', employee: e })}
                                disabled={!canExecuteTenantActions}
                                className="text-sm text-error hover:underline font-medium disabled:opacity-50"
                              >
                                Delete
                              </button>
                            </>
                          ) : e.status !== 'offboarded' ? (
                            <button
                              type="button"
                              onClick={() => setRowAction({ kind: 'suspend', employee: e })}
                              disabled={!canExecuteTenantActions}
                              className="text-sm text-error hover:underline font-medium disabled:opacity-50"
                            >
                              Suspend
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      ) : null}

      {createOpen ? (
        <CreateEmployeeModal
          schedules={schedules}
          accessZones={accessZones}
          scopeOpts={scopeOpts}
          onCancel={() => setCreateOpen(false)}
          onDone={(msg) => {
            setCreateOpen(false);
            setToast(msg);
            reload();
          }}
        />
      ) : null}

      {bulkOpen ? (
        <BulkImportModal
          scopeOpts={scopeOpts}
          onCancel={() => setBulkOpen(false)}
          onDone={(msg) => {
            setBulkOpen(false);
            setToast(msg);
            reload();
          }}
        />
      ) : null}

      {rowAction ? (
        <RowActionModal
          state={rowAction}
          scopeOpts={scopeOpts}
          onCancel={() => setRowAction(null)}
          onDone={(msg) => {
            setRowAction(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

// Kept for future use when per-row action buttons are re-introduced.
// Currently the table uses a simple "Edit →" link; this full action bar is unused.
function _RowActions({
  employee,
  tenantId,
  onAction,
}: {
  employee: EmployeeRow;
  tenantId: string | null;
  onAction: (kind: RowActionKind) => void;
}) {
  const disabled = employee.status === 'offboarded';
  const canIssueCredential =
    employee.status !== 'offboarded' &&
    employee.status !== 'suspended' &&
    employee.sync_status === 'synced';

  return (
    <div className="flex items-center justify-end gap-1">
      <Link
        to={
          tenantId
            ? `/employees/${employee.id}?tenantId=${encodeURIComponent(tenantId)}&edit=1`
            : '#'
        }
        aria-label={`Edit ${employee.name}`}
        title={disabled ? 'Employee is deleted' : 'Edit employee'}
        aria-disabled={disabled}
        data-testid={`employee-row-edit-${employee.id}`}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high ${
          disabled ? 'pointer-events-none opacity-30' : ''
        }`}
      >
        <Icon name="edit" className="text-sm" />
      </Link>
      <Link
        to={
          tenantId
            ? `/employees/${employee.id}?tenantId=${encodeURIComponent(tenantId)}&issueCredential=1`
            : '#'
        }
        aria-label={`Issue Phone KeyPass for ${employee.name}`}
        title={
          canIssueCredential
            ? 'Issue Phone KeyPass'
            : 'Open employee credentials; employee must be ready and active to issue'
        }
        data-testid={`employee-row-credentials-${employee.id}`}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high"
      >
        <Icon name="key" className="text-sm" />
      </Link>
      <Link
        to={`/credentials/enroll${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}&employeeId=${encodeURIComponent(employee.id)}` : `?employeeId=${encodeURIComponent(employee.id)}`}`}
        aria-label={`Enroll credential for ${employee.name}`}
        title="Enroll credential at a reader"
        data-testid={`employee-row-enroll-${employee.id}`}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high"
      >
        <Icon name="contactless" className="text-sm" />
      </Link>
      {employee.status === 'suspended' ? (
        <button
          type="button"
          onClick={() => onAction('reactivate')}
          title="Reactivate employee"
          aria-label={`Reactivate ${employee.name}`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high"
          data-testid={`employee-row-reactivate-${employee.id}`}
        >
          <Icon name="refresh" className="text-sm" />
        </button>
      ) : employee.status === 'active' || employee.status === 'invited' ? (
        <button
          type="button"
          onClick={() => onAction('suspend')}
          title="Suspend employee"
          aria-label={`Suspend ${employee.name}`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high"
          data-testid={`employee-row-suspend-${employee.id}`}
        >
          <Icon name="pause" className="text-sm" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onAction('offboard')}
        disabled={disabled}
        title={disabled ? 'Employee is already deleted' : 'Delete employee'}
        aria-label={`Delete ${employee.name}`}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline-variant bg-surface-container-lowest text-error hover:bg-error-container disabled:opacity-30"
        data-testid={`employee-row-offboard-${employee.id}`}
      >
        <Icon name="person_remove" className="text-sm" />
      </button>
    </div>
  );
}

interface CreateEmployeeModalProps {
  schedules: ScheduleSummary[];
  accessZones: AccessZoneSummary[];
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}

export function CreateEmployeeModal({
  schedules,
  accessZones,
  scopeOpts,
  onCancel,
  onDone,
}: CreateEmployeeModalProps) {
  const { state } = useAuth();
  const thirdPartyNfcEnabled =
    state.status === 'authed' && state.me.credential_policy.third_party_nfc_enabled;
  const enrollmentReaderMode =
    state.status === 'authed' ? state.me.credential_policy.enrollment_reader_mode : 'any';
  const enrollmentReaderId =
    state.status === 'authed' ? state.me.credential_policy.enrollment_reader_id : null;
  const enrollmentReaderName =
    state.status === 'authed' ? state.me.credential_policy.enrollment_reader_name : null;

  // One idempotency-key per modal lifetime: a retry inside the same
  // open modal reuses the key so the backend collapses duplicate creates.
  const { key } = useIdempotencyKey();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<EmployeeRole>('employee');
  const [scheduleId, setScheduleId] = useState<string>('');
  const [selectedAccessZoneIds, setSelectedAccessZoneIds] = useState<string[]>([]);
  const [assignedAccessZoneIds, setAssignedAccessZoneIds] = useState<string[]>([]);
  const [enrollFobNow, setEnrollFobNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdEmployee, setCreatedEmployee] = useState<{
    id: string;
    syncStatus: SyncStatus;
  } | null>(null);
  const [setupIncomplete, setSetupIncomplete] = useState(false);
  // Once the employee is created, if the operator asked to enroll a fob now
  // we hand off to EnrollFobModal for that employee instead of closing.
  const [enrollFor, setEnrollFor] = useState<{
    employeeId: string;
    personName: string;
    baseToast: string;
  } | null>(null);

  function toggleAccessZone(id: string) {
    setSelectedAccessZoneIds((current) =>
      current.includes(id) ? current.filter((zoneId) => zoneId !== id) : [...current, id],
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (createdEmployee && !setupIncomplete && createdEmployee.syncStatus !== 'synced') {
      onDone('Employee saved. Controller sync is pending; it is not ready for fob enrollment yet.');
      return;
    }
    if (name.trim().length === 0 || email.trim().length === 0) {
      setError('Name and email are required.');
      return;
    }
    if (!scheduleId) {
      setError('Schedule is required before adding an employee.');
      return;
    }
    setSubmitting(true);
    setError(null);
    let employee = createdEmployee;
    try {
      if (!employee) {
        const created = await apiClient.withIdempotencyKey(key).post<{
          data: { id: string; sync_status?: SyncStatus };
        }>(
          '/api/v1/employees',
          {
            email: email.trim().toLowerCase(),
            name: name.trim(),
            role,
            schedule_id: scheduleId,
          },
          scopeOpts,
        );
        employee = {
          id: created.data.id,
          // Treat an omitted status conservatively: fob enrollment requires
          // an explicit controller-sync confirmation.
          syncStatus: created.data.sync_status ?? 'pending',
        };
        setCreatedEmployee(employee);
      }

      const assigned = new Set(assignedAccessZoneIds);
      for (const zoneId of selectedAccessZoneIds) {
        if (assigned.has(zoneId)) continue;
        await apiClient.post(`/api/v1/employees/${employee.id}/access-zones/${zoneId}`, {}, scopeOpts);
        assigned.add(zoneId);
        setAssignedAccessZoneIds([...assigned]);
      }

      if (employee.syncStatus !== 'synced') {
        setSetupIncomplete(false);
        setSubmitting(false);
        return;
      }

      setSetupIncomplete(false);
      const zoneCount = selectedAccessZoneIds.length;
      const baseToast =
        zoneCount > 0
          ? `Employee created and ${zoneCount} access zone${zoneCount === 1 ? '' : 's'} assigned.`
          : 'Employee created.';
      if (enrollFobNow && thirdPartyNfcEnabled) {
        setEnrollFor({ employeeId: employee.id, personName: name.trim(), baseToast });
        setSubmitting(false);
      } else {
        onDone(baseToast);
      }
    } catch (err) {
      if (employee) {
        // A successful employee POST followed by a failed zone POST leaves a
        // recoverable setup state; retain the ID and retry only the zones.
        setSetupIncomplete(true);
        setError(
          'Employee saved, but setup is incomplete. Retry the access-zone assignments before closing.',
        );
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to create employee or assign access.');
      }
      setSubmitting(false);
    }
  }

  function handleCancel() {
    if (createdEmployee) {
      onDone(
        setupIncomplete
          ? 'Employee saved, but setup is incomplete.'
          : createdEmployee.syncStatus !== 'synced'
            ? 'Employee saved. Controller sync is pending.'
            : 'Employee created.',
      );
      return;
    }
    onCancel();
  }

  const syncPending = createdEmployee !== null && createdEmployee.syncStatus !== 'synced';

  if (enrollFor) {
    return (
      <EnrollFobModal
        empId={enrollFor.employeeId}
        personName={enrollFor.personName}
        thirdPartyNfcEnabled={thirdPartyNfcEnabled}
        enrollmentReaderMode={enrollmentReaderMode}
        enrollmentReaderId={enrollmentReaderId}
        enrollmentReaderName={enrollmentReaderName}
        scopeOpts={scopeOpts}
        onCancel={() => onDone(enrollFor.baseToast)}
        onDone={(msg) => onDone(`${enrollFor.baseToast} ${msg}`)}
      />
    );
  }

  return (
    <ModalShell
      title="Add employee"
      onCancel={handleCancel}
      testId="create-employee-modal"
      submitting={submitting}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Name" htmlFor="emp-name">
          <input
            id="emp-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={createdEmployee !== null}
            maxLength={120}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
            data-testid="employee-name-input"
          />
        </Field>
        <Field label="Email" htmlFor="emp-email">
          <input
            id="emp-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={createdEmployee !== null}
            maxLength={254}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
            data-testid="employee-email-input"
          />
        </Field>
        <Field label="Role" htmlFor="emp-role">
          <select
            id="emp-role"
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
            disabled={createdEmployee !== null}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
          </select>
        </Field>
        <Field label="Schedule" htmlFor="emp-sched">
          <select
            id="emp-sched"
            value={scheduleId}
            onChange={(e) => setScheduleId(e.target.value)}
            required
            disabled={createdEmployee !== null}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
          >
            <option value="" disabled>
              Select a schedule
            </option>
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {schedules.length === 0 ? (
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              Create a schedule before adding employees.
            </p>
          ) : null}
        </Field>
        <Field label="Access zones (optional)" htmlFor="employee-access-zones">
          <div
            id="employee-access-zones"
            className="max-h-52 overflow-y-auto rounded border border-outline-variant bg-surface"
          >
            {accessZones.length === 0 ? (
              <div className="px-3 py-2 font-body-sm text-body-sm text-on-surface-variant">
                No access zones available.
              </div>
            ) : (
              accessZones.map((zone) => (
                <label
                  key={zone.id}
                  className="flex items-center gap-3 border-b border-outline-variant px-3 py-2 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedAccessZoneIds.includes(zone.id)}
                    onChange={() => toggleAccessZone(zone.id)}
                    disabled={createdEmployee !== null}
                    className="h-4 w-4 accent-primary"
                    data-testid={`employee-access-zone-${zone.id}`}
                  />
                  <span className="min-w-0 flex-1 font-body-md text-body-md text-on-surface">
                    {zone.name}
                  </span>
                </label>
              ))
            )}
          </div>
        </Field>
        {thirdPartyNfcEnabled ? (
          <Field label="Enroll fob now?" htmlFor="enroll-fob-now-yes">
            <div className="flex items-center gap-4" role="radiogroup" aria-label="Enroll fob now?">
              <label className="inline-flex items-center gap-2">
                <input
                  id="enroll-fob-now-yes"
                  type="radio"
                  name="enroll-fob-now"
                  checked={enrollFobNow}
                  onChange={() => setEnrollFobNow(true)}
                  className="h-4 w-4 accent-primary"
                  data-testid="enroll-fob-now-yes"
                />
                Yes
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  id="enroll-fob-now-no"
                  type="radio"
                  name="enroll-fob-now"
                  checked={!enrollFobNow}
                  onChange={() => setEnrollFobNow(false)}
                  className="h-4 w-4 accent-primary"
                  data-testid="enroll-fob-now-no"
                />
                No
              </label>
            </div>
            {enrollFobNow ? (
              <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
                Stand near enrollment reader now to enroll fob.
              </p>
            ) : null}
          </Field>
        ) : null}
        <input type="hidden" value={key} data-testid="idempotency-key" readOnly />
        {setupIncomplete ? (
          <div
            role="alert"
            data-testid="employee-setup-incomplete"
            className="text-error font-body-md text-body-md"
          >
            Employee saved, but setup is incomplete. Retry the access-zone assignments before closing.
          </div>
        ) : null}
        {syncPending ? (
          <div
            role="status"
            data-testid="employee-sync-pending"
            className="text-on-surface-variant font-body-md text-body-md"
          >
            Employee saved, but controller sync is pending. It is not ready for fob enrollment yet.
          </div>
        ) : null}
        {error && !setupIncomplete && !syncPending ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        <ModalFooter
          onCancel={handleCancel}
          submitting={submitting}
          submitDisabled={!scheduleId}
          submitLabel={setupIncomplete ? 'Retry setup' : syncPending ? 'Close' : 'Add'}
          submitTestId={setupIncomplete ? 'employee-setup-retry' : 'employee-create-submit'}
        />
      </form>
    </ModalShell>
  );
}

interface BulkImportRow {
  email: string;
  name: string;
  role: EmployeeRole;
  schedule_id?: string;
}

interface BulkImportResult {
  dry_run: boolean;
  total: number;
  valid: number;
  errors: Array<{ index: number; email: string; code: string; message: string }>;
  created: Array<{ id: string; email: string }>;
}

function parseBulkRows(text: string): {
  rows: BulkImportRow[];
  parseError: string | null;
} {
  const trimmed = text.trim();
  if (trimmed.length === 0) return { rows: [], parseError: 'Provide rows to import.' };
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (!Array.isArray(parsed)) return { rows: [], parseError: 'Pasted list must be an array.' };
      const rows: BulkImportRow[] = [];
      for (const r of parsed) {
        const row = r as Record<string, unknown>;
        rows.push({
          email: String(row.email ?? ''),
          name: String(row.name ?? ''),
          role: (row.role === 'manager' ? 'manager' : 'employee') as EmployeeRole,
          ...(typeof row.schedule_id === 'string' && row.schedule_id.length > 0
            ? { schedule_id: row.schedule_id }
            : {}),
        });
      }
      return { rows, parseError: null };
    } catch (err) {
      return { rows: [], parseError: `Invalid pasted list: ${(err as Error).message}` };
    }
  }
  // CSV: header row optional. Columns: email,name,role,schedule_id.
  // schedule_id is optional for employee-only roster imports.
  const lines = trimmed
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], parseError: 'Empty input.' };
  let headerSeen = false;
  let cols: string[] = ['email', 'name', 'role', 'schedule_id'];
  const firstLine = lines[0]!;
  if (/email/i.test(firstLine) && /name/i.test(firstLine)) {
    cols = firstLine.split(',').map((c) => c.trim().toLowerCase());
    headerSeen = true;
  }
  const rows: BulkImportRow[] = [];
  for (const line of headerSeen ? lines.slice(1) : lines) {
    const parts = line.split(',').map((c) => c.trim());
    const rec: Record<string, string> = {};
    /* eslint-disable security/detect-object-injection */
    for (let i = 0; i < cols.length; i++) {
      const colName = cols[i];
      if (typeof colName === 'string') {
        // colName is sourced from a small fixed header set; bracket
        // write is safe.
        rec[colName] = parts[i] ?? '';
      }
    }
    /* eslint-enable security/detect-object-injection */
    rows.push({
      email: rec.email ?? '',
      name: rec.name ?? '',
      role: rec.role === 'manager' ? 'manager' : 'employee',
      ...(rec.schedule_id && rec.schedule_id.length > 0
        ? { schedule_id: rec.schedule_id }
        : {}),
    });
  }
  return { rows, parseError: null };
}

export function BulkImportModal({
  scopeOpts,
  onCancel,
  onDone,
}: {
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [text, setText] = useState('');
  const [dryRun, setDryRun] = useState<BulkImportResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runDryRun() {
    setError(null);
    setDryRun(null);
    const { rows, parseError } = parseBulkRows(text);
    if (parseError) {
      setError(parseError);
      return;
    }
    if (rows.length === 0) {
      setError('No rows to import.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post<{ data: BulkImportResult }>(
        '/api/v1/employees/bulk',
        { dry_run: true, rows },
        scopeOpts,
      );
      setDryRun(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Preview failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function commit() {
    if (!dryRun) return;
    if (dryRun.errors.length > 0) {
      setError('Preview has errors - fix them before importing.');
      return;
    }
    const { rows } = parseBulkRows(text);
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient
        .withIdempotencyKey(key)
        .post<{ data: BulkImportResult }>(
          '/api/v1/employees/bulk',
          { dry_run: false, rows },
          scopeOpts,
        );
      onDone(`Imported ${res.data.created.length} employee(s).`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title="Bulk import employees"
      onCancel={onCancel}
      testId="bulk-import-modal"
      wide
      submitting={submitting}
    >
      <div className="space-y-4">
        <p className="font-body-md text-body-md text-on-surface-variant">
          Paste a CSV with columns <code>email,name,role,schedule_id</code> or a structured list.
          Leave schedule_id blank for employee-only imports. Preview it first; rows with problems
          must be fixed before importing.
        </p>
        <textarea
          rows={8}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setDryRun(null);
          }}
          placeholder={
            'email,name,role,schedule_id\nalice@example.com,Alice,employee,\nbob@example.com,Bob,manager,'
          }
          className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface font-code text-code"
          data-testid="bulk-import-textarea"
        />
        <input type="hidden" value={key} data-testid="bulk-idempotency-key" readOnly />
        {error ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        {dryRun ? (
          <div
            className="rounded border border-outline-variant p-3 space-y-2"
            data-testid="bulk-preview-result"
          >
            <div className="font-label-md text-label-md uppercase text-on-surface-variant">
              Preview summary
            </div>
            <div className="font-body-md text-body-md text-on-surface">
              Total: {dryRun.total} · Valid: {dryRun.valid} · Errors:{' '}
              {dryRun.errors.length}
            </div>
            {dryRun.errors.length > 0 ? (
              <ul className="font-code text-code text-error max-h-40 overflow-auto">
                {dryRun.errors.map((er, i) => (
                  <li key={i}>
                    row {er.index} ({er.email}) — {er.code}: {er.message}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-on-surface-variant font-body-md text-body-md">
                All rows look valid. Click <strong>Import</strong> to continue.
              </div>
            )}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={runDryRun} disabled={submitting}>
            Preview import
          </Button>
          <Button
            variant="primary"
            onClick={commit}
            disabled={submitting || !dryRun || dryRun.errors.length > 0}
            data-testid="bulk-import-btn"
          >
            Import
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

interface RowActionState {
  kind: RowActionKind;
  employee: EmployeeRow;
}

function RowActionModal({
  state,
  scopeOpts,
  onCancel,
  onDone,
}: {
  state: RowActionState;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { kind, employee } = state;
  const { key } = useIdempotencyKey();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const headline =
    kind === 'suspend'
      ? `Suspend ${employee.name}?`
      : kind === 'reactivate'
        ? `Restore access for ${employee.name}?`
        : `Permanently remove ${employee.name}?`;
  const body =
    kind === 'offboard'
      ? 'This removes the person from live doors and revokes their credentials so fobs can be reassigned. The portal record is retained for history.'
      : kind === 'suspend'
        ? "Their access stops right away. Their credentials stay assigned for audit, so denied attempts can still be tied back to this person."
        : 'They get their access back right away. If their account was already removed from live doors, you will need to re-invite them instead.';
  const irreversible = kind === 'offboard';

  async function go() {
    setSubmitting(true);
    setError(null);
    try {
      const targetStatus =
        kind === 'suspend'
          ? 'suspended'
          : kind === 'reactivate'
            ? 'active'
            : 'offboarded';
      await apiClient
        .withIdempotencyKey(key)
        .patch(`/api/v1/employees/${employee.id}`, { status: targetStatus }, scopeOpts);
      onDone(
        kind === 'suspend'
          ? `${employee.name} has been suspended.`
          : kind === 'reactivate'
            ? `${employee.name} has access again.`
            : `${employee.name} has been removed.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={headline}
      onCancel={onCancel}
      testId={`row-action-${kind}-modal`}
      destructive={irreversible}
      submitting={submitting}
    >
      <div className="space-y-4">
        <p className="font-body-md text-body-md text-on-surface">{body}</p>
        {irreversible ? (
          <label className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="offboard-confirm-check"
            />
            I understand this releases active credentials for reassignment.
          </label>
        ) : null}
        <input type="hidden" value={key} data-testid={`${kind}-idempotency-key`} readOnly />
        {error ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={irreversible ? 'danger' : 'primary'}
            onClick={go}
            disabled={submitting || (irreversible && !confirmed)}
            data-testid={`row-action-${kind}-confirm`}
          >
            {kind === 'suspend' ? 'Suspend' : kind === 'reactivate' ? 'Reactivate' : 'Delete'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalFooter({
  onCancel,
  submitting,
  submitDisabled,
  submitLabel,
  submitTestId,
}: {
  onCancel: () => void;
  submitting: boolean;
  submitDisabled?: boolean;
  submitLabel: string;
  submitTestId?: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="ghost" onClick={onCancel} disabled={submitting} type="button">
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        disabled={submitting || submitDisabled}
        data-testid={submitTestId}
      >
        {submitting ? 'Working…' : submitLabel}
      </Button>
    </div>
  );
}
